/**
 * CalendarWidget
 *
 * Compact sidebar widget showing today's calendar events.
 * Only renders when Google is connected AND events exist for today.
 * Max 5 events, sorted by start time.
 * Danny-scheduled events get a ✦ prefix.
 */

import { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { getTodayCalendarEvents, type CalendarEvent } from '../api/calendar';
import { getGoogleIntegrationStatus } from '../api/integrations';


const MAX_EVENTS = 5;

function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function EventRow({ event }: { event: CalendarEvent }) {
  const timeLabel = event.isAllDay
    ? 'All day'
    : `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`;

  const inner = (
    <div
      className={clsx(
        'flex flex-col gap-0.5 px-2 py-1.5 rounded transition-colors',
        event.gcalUrl
          ? 'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark cursor-pointer'
          : 'cursor-default',
        event.isDeclined && 'opacity-50',
      )}
    >
      <span className="font-mono text-[11px] text-text-tertiary-light dark:text-text-tertiary-dark leading-tight">
        {timeLabel}
      </span>
      <span className="font-mono text-[12px] text-text-primary-light dark:text-text-primary-dark leading-tight truncate">
        {event.isDannyScheduled && (
          <span className="text-primary-600 dark:text-primary-400 mr-1">✦</span>
        )}
        {event.title}
      </span>
    </div>
  );

  if (event.gcalUrl) {
    return (
      <a
        href={event.gcalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block no-underline"
        title={`Open "${event.title}" in Google Calendar`}
      >
        {inner}
      </a>
    );
  }

  return <div>{inner}</div>;
}

interface CalendarWidgetProps {
  collapsed?: boolean;
  onOpenSettings?: (tab: string) => void;
  /** Bump this to force a refetch (e.g. after closing settings where user may have connected). */
  refreshNonce?: number;
}

export function CalendarWidget({ collapsed = false, onOpenSettings, refreshNonce = 0 }: CalendarWidgetProps) {
  const { user } = useAuth();
  const { calendarDone, onboardingComplete } = useOnboarding();
  const userId = user?.id ?? null;
  const dismissKey = userId ? `danny_dismissed_calendar_connect_${userId}` : null;

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Re-read dismissed state whenever the user changes (login, switch account)
  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(localStorage.getItem(dismissKey) === 'true');
  }, [dismissKey]);

  const load = useCallback(async () => {
    // Step 1: connection status — definitive source of truth
    let connected: boolean;
    try {
      const status = await getGoogleIntegrationStatus();
      connected = !!status.connected;
    } catch {
      connected = false;
    }
    setIsConnected(connected);

    // Step 2: only if connected, try to fetch events. Event fetch failure
    // does NOT change connection state.
    if (connected) {
      try {
        const todayEvents = await getTodayCalendarEvents();
        const visible = todayEvents.filter((e) => !e.isDeclined).slice(0, MAX_EVENTS);
        setEvents(visible);
      } catch {
        setEvents([]);
      }
    } else {
      setEvents([]);
    }

    setIsLoaded(true);
  }, []);

  // Fetch only after user is authenticated. Re-fetch when userId or refreshNonce changes.
  useEffect(() => {
    if (!userId) {
      setIsLoaded(false);
      setIsConnected(null);
      return;
    }
    void load();

    // Re-check after Google OAuth redirect lands
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connect') === 'success') {
      const timer = setTimeout(() => void load(), 1500);
      return () => clearTimeout(timer);
    }
  }, [userId, load, refreshNonce]);

  // Not yet resolved — render nothing
  if (!isLoaded) return null;

  // Not connected — show amber connect prompt (dismissable; only when sidebar expanded).
  // BUT: suppress while the GettingStartedChecklist is still showing — its
  // "Connect Google Calendar" item already provides the same CTA. Once the
  // user dismisses the checklist (onboardingComplete OR calendarDone), this
  // amber prompt takes over as the persistent reminder.
  if (!isConnected) {
    if (collapsed || dismissed) return null;
    if (!onboardingComplete && !calendarDone) return null;
    const handleDismiss = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (dismissKey) localStorage.setItem(dismissKey, 'true');
      setDismissed(true);
    };
    return (
      <div className="mx-1.5 mb-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
              Calendar not connected.{' '}
              <button
                type="button"
                onClick={() => onOpenSettings?.('integrations')}
                className="underline hover:no-underline cursor-pointer"
              >
                Connect
              </button>
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            title="Dismiss"
            aria-label="Dismiss"
            className="shrink-0 p-0.5 -m-0.5 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 cursor-pointer"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  // Connected but no events today — render nothing
  if (events.length === 0) return null;

  // Sidebar collapsed — show only a calendar icon dot indicator
  if (collapsed) {
    return (
      <div
        className="flex justify-center py-1.5 mb-1"
        title={`${events.length} event${events.length !== 1 ? 's' : ''} today`}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-primary-500 dark:bg-primary-400" />
      </div>
    );
  }

  return (
    <div className="mx-1.5 mb-2 border border-border-subtle-light dark:border-border-subtle-dark rounded-lg overflow-hidden">
      <div className="px-3 py-1.5 border-b border-border-subtle-light dark:border-border-subtle-dark">
        <span className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark">
          Today
        </span>
      </div>
      <div className="py-0.5">
        {events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}
